/* @flow */
import { replace } from '../utils/dom';
import {
  shouldRedirectOnClient,
  getRedirectPathForClient
} from '../utils/redirect';
import axios from 'axios';
import {pickBy, identity} from 'lodash';
import type { Action } from './types';

const AXIOS_NETWORK_ERROR_MESSAGE = 'Network Error';

/*
 * Redirects -- like ones created in the Redirect Tool, or our geolocation-based
 * redirect logic -- can cause one of these two errors to be thrown.
 */
export const JSON_PARSE_ERROR = new RangeError(
  'The JSON response from the API could not be parsed.'
);
export const DOMAIN_CHANGED_ERROR = new RangeError(
  'The API request got redirected to a different domain.'
);

export const FETCH_FOUNDRY_RESPONSE = 'FETCH_FOUNDRY_RESPONSE';
export const RECEIVE_FOUNDRY_RESPONSE = 'RECEIVE_FOUNDRY_RESPONSE';
export const RECEIVE_MORE_CONTENT_FOUNDRY_RESPONSE = 'RECEIVE_MORE_CONTENT_FOUNDRY_RESPONSE';
export const RECEIVE_BAD_FOUNDRY_RESPONSE = 'RECEIVE_BAD_FOUNDRY_RESPONSE';

export const receiveFoundryResponse = (
  { status, headers, data: { result, pagination = {} } }: ResponseOptions
): Action => {
  return pickBy({
    type: RECEIVE_FOUNDRY_RESPONSE,
    data: result,
    headers,
    status,
    pagination
  }, identity);
};

/* eslint-disable no-unused-vars */
export const receiveMoreContentFoundryResponse = (
  { status, headers, data: { result, pagination = {} } }: ResponseOptions
): Action => pickBy({
  type: RECEIVE_MORE_CONTENT_FOUNDRY_RESPONSE,
  data: result,
  status,
  pagination
}, identity);
/* eslint-enable no-unused-vars */

export const receiveBadFoundryResponse = (
  status: ?number,
  errorMessage: string
): Action => ({
  type: RECEIVE_BAD_FOUNDRY_RESPONSE,
  status,
  errorMessage
});

export const fetchFoundryResponse =
  (apiUrl: string, fetchType: FetchType): Function =>
    (dispatch: Function): Promise<void> => {
      dispatch({ type: FETCH_FOUNDRY_RESPONSE, fetchType });
      return axios.get(apiUrl).then(
        ({ data, headers, status }: ResponseOptions): void => {
          /*
           * See js/utils/redirect.js for clarification on when this client-side
           * redirect is necessary
           */
          if (shouldRedirectOnClient(data, fetchType)) {
            replace(getRedirectPathForClient(data));
          }

          try {
            if (fetchType === 'moreContent') {
              dispatch(receiveMoreContentFoundryResponse({ data, headers, status }));
            } else {
              dispatch(receiveFoundryResponse({ data, headers, status }));
            }
          } catch (e) {
            /*
             * Failed to parse JSON, probably because of a redirect.
             * Handled in routes.js.
             */
            throw JSON_PARSE_ERROR;
          }
        },
        ({ message, response: { status } = { status: null } }: ResponseError): void => {
          if (message === AXIOS_NETWORK_ERROR_MESSAGE) {
            /*
             * Somehow the API request ran into a CORS issue, probably
             * because of a redirect.
             * Handled in routes.js.
             */
            throw DOMAIN_CHANGED_ERROR;
          }
          dispatch(receiveBadFoundryResponse(status, message));
        }
      );
    };

type ResponseOptions = {
  data: {
    result: Object,
    pagination: Pagination
  },
  headers: ?Object,
  status: ?number
};

type ResponseError = {
  message: string,
  response: ResponseOptions
};

export type Pagination = {
  prev?: string,
  next?: string
};

export type FetchType = 'fetchingNewPage' | 'fetchingOnSamePage' | 'moreContent';
